//The value is meaningless. We just want the list to be in a hash so we can look it up faster than iterating through an array.
const commonEventNames = {
    'route-change': 1,
    'app-shell-navigate': 1
};
/**
 * Adds an event listener that listens for mfe events by name.
 * @param eventName: The name of the event to listen for.  By default, the callback will
 * be called with the last event fired with that name if it exists.
 * Events prefixed with '!' will never be called immediately upon subscription (because the last event won't be cached).
 * The event name needs to have a '.' character in it as well. We recommend {context}.{event-description}
 * @param callback: The callback function you would like called when the event fires.
 * @param ignoreCachedEvent: When you set this to true, it will prevent the callback from being called immediately upon subscription with the last event fired.
 * @returns A remove listener function.  Call it when you no longer wish to receive mfe events.
 * Please make sure to call this when your React component unloads.
 * @note If you need to pass in non-changing data into an MFE, send it through the MfeInitFunctionConfig.
 * See DynamicComponent or initializeMfe() and psMfe() for more information on doing that.
 */
export function addMfeEventListener(eventName, callback, ignoreCachedEvent) {
    if (!isEventNameValid(eventName)) {
        console.error(`The eventName ${eventName} is not valid. It must have at least one '.' in it or be one of the standard event names.`);
    }
    //First check the cache to see if the callback should be called immediately.
    const priorTransferEvent = getEventCache()[eventName];
    if (priorTransferEvent != null && ignoreCachedEvent !== true) {
        callback(convertToMfeEvent(priorTransferEvent));
    }
    //Now create the listener callback and subscribe to the event.
    const listenerCallback = (event) => {
        callback(convertToMfeEvent(event.detail));
    };
    document.body.addEventListener(eventName, listenerCallback);
    //Returns an unsubscribe function
    return () => {
        document.body.removeEventListener(eventName, listenerCallback);
    };
}
/**
 * Dispatches an mfe event. Note that you need to pass in your unique mfe name when you call this so
 * anyone debugging can tell where the event came from.
 * @param mfeName: Syncs up with ModuleFederation 'name'
 * @param eventName: The name of the event to fire.  The even name needs to have a '.' character in it as well. We recommend {context}.{event-description}.
 * By default the last event fired is cached. If the event name is prefixed with the '!' character, it will not be cached.
 * @param data: Any data that you wish to pass along.
 * By default it will be serialized to a JSON string and then deserialized by the listener callback.
 * This prevents anyone from messing with your MFE's JavaScript objects.
 * @param isUnguarded: Set this to true if you do not want your data to be serialized. This allows
 * other MFEs to potentially change your JavaScript objects or call their functions so be careful about setting this to true.
 *
 * @note If you need to pass in non-changing data into an MFE, send it through the MfeInitFunctionConfig.
 * See DynamicComponent or initializeMfe() and psMfe() for more information on doing that.
 *
 * @example dispatchMfeEvent('my-mfe-id', 'assignment-service.selected-assignment-change', { selectedAssignment: getSelectedAssignment() });
 * @example The presence of the '!' means that the event will not cached so it will not be dispatched to all new listeners when they subscribe.
 * dispatchMfeEvent('my-mfe-id', '!student-service.selected-student-change', { selectedStudent: getSelectedStudent() });
 */
export function dispatchMfeEvent(mfeName, eventName, data, isUnguarded) {
    if (!isEventNameValid(eventName)) {
        console.error(`The eventName ${eventName} is not valid. It must have at least one '.' in it or be one of the standard event names.`);
    }
    const mfeTransferEvent = {
        name: eventName,
        sourceMfe: mfeName,
        isGuarded: isUnguarded !== true
    };
    if (isUnguarded === true) {
        mfeTransferEvent.unguardedContext = data;
    }
    else {
        mfeTransferEvent.jsonString = JSON.stringify(data);
    }
    if (isEventCached(eventName)) {
        getEventCache()[eventName] = mfeTransferEvent;
    }
    document.body.dispatchEvent(new CustomEvent(eventName, { detail: mfeTransferEvent }));
}
function getEventCache() {
    if (window.mfeUtilsEventCache == null) {
        window.mfeUtilsEventCache = {};
    }
    return window.mfeUtilsEventCache;
}
function convertToMfeEvent(transferEvent) {
    const event = {
        name: transferEvent.name,
        sourceMfe: transferEvent.sourceMfe,
        context: undefined
    };
    if (transferEvent.isGuarded) {
        try {
            if (transferEvent.jsonString != null) {
                event.context = JSON.parse(transferEvent.jsonString);
            }
        }
        catch (e) {
            console.error('An error occurred while parsing the event data');
        }
    }
    else {
        event.context = transferEvent.unguardedContext;
    }
    return event;
}
/**
 * Event names must have at least one '.' character in them. It must not be the first or last character in the name.
 * The name can have a ! as the first character to show that the event will not be cached.
 * @param: eventName The MFE event name to test
 * @returns true if the even name is valid. false otherwise
 */
export function isEventNameValid(eventName) {
    if (commonEventNames[eventName] != null) {
        return true;
    }
    return /^!?[^.!]+(\.[^.]+)+$/.test(eventName);
}
/**
 * Checks to see if the event should be cached.  It will NOT be cached if it starts with a '!' character.
 * @param eventName: The name of the event to test
 * @returns false if the evenName starts with a '!'. true otherwise.
 */
export function isEventCached(eventName) {
    return !/^!/.test(eventName);
}
